import { reportIntentPageSlugs } from "@ishvaram/domain-data/report-intent-slugs";

/**
 * Route predicates for report conversion landings, built from the slug-only
 * projection so they are safe to import from the root layout's client graph
 * (global overlays, MobileBottomBar, StickyChatButton, ClientEnhancements).
 *
 * `@/lib/report-intent-data` carries the full page copy for the report routes
 * themselves; importing it here shipped every intent page's copy to every
 * route on the site (measured 2026-09-12: ~30 KB gzip per page).
 */
export type ReportIntentPageSlug = (typeof reportIntentPageSlugs)[number];

export const REPORT_INTENT_PAGE_SLUGS: readonly ReportIntentPageSlug[] = reportIntentPageSlugs;

/**
 * Report landings that also ship a served `/hi/` twin. /hi/dosha-report/ is not
 * one: it 301s to /hi/dosha-remedy-plan/ since the 2026-09-30 cleanup release.
 */
export const HINDI_REPORT_INTENT_SLUGS: ReadonlySet<ReportIntentPageSlug> =
  new Set<ReportIntentPageSlug>(["career-report", "marriage-report", "dosha-remedy-plan"]);

const REPORT_INTENT_SLUG_SET: ReadonlySet<string> = new Set<string>(reportIntentPageSlugs);

export function isReportIntentSlug(value: string): value is ReportIntentPageSlug {
  return REPORT_INTENT_SLUG_SET.has(value);
}

/** True only for an existing report conversion landing, including Hindi twins. */
export function isReportIntentLandingPath(pathname: string): boolean {
  const path = pathname.split(/[?#]/, 1)[0].replace(/\/+$/, "") || "/";
  const segments = path.split("/").filter(Boolean);
  if (segments.length === 1) return isReportIntentSlug(segments[0]);
  return (
    segments.length === 2 &&
    segments[0] === "hi" &&
    isReportIntentSlug(segments[1]) &&
    HINDI_REPORT_INTENT_SLUGS.has(segments[1])
  );
}
